/**
 * Author: Devon
 *
 * Component: BoxLoader | FullScreenLoader
 * Description:
 * Loader module provides loading components for use in the application.
 * It includes a basic loader and two variations: a centered box loader
 * and a full-screen loader. These components utilize Chakra UI's Spinner
 * for visual indication of loading states.
 */

import type { SystemStyleObject } from '@chakra-ui/react';
import { Spinner } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';

import MotionBox from 'watchtower-ui/components/common/MotionBox';

type LoaderProps = {
  size?: string;
};
type BoxLoaderProps = LoaderProps & { sx?: SystemStyleObject; id?: string; hide?: boolean };
type FSLoaderProps = BoxLoaderProps & {
  full?: boolean;
};
const Loader = ({ size = 'xl' }: LoaderProps) => <Spinner size={size} color="#f7ff00" />;

export const BoxLoader = ({ sx, id, hide = false, ...rest }: BoxLoaderProps) => (
  <AnimatePresence>
    <MotionBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx,
      }}
      id={id}
      initial={{ opacity: 1 }}
      animate={{ opacity: hide ? 0 : 1 }}
      exit={{ opacity: 0 }}
      pointerEvents={hide ? 'none' : 'auto'}
    >
      <Loader {...rest} />
    </MotionBox>
  </AnimatePresence>
);

export const FullScreenLoader = ({ sx, full, ...props }: FSLoaderProps) => (
  <BoxLoader
    sx={{
      ...sx,
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 999,
      width: '100%',
      height: '100%',
      backgroundColor: full ? '#000 !important' : '#0006',
    }}
    {...props}
  />
);
